import { RouteType } from "../@types/RouteType";
import { Navigate } from "react-router-dom";

// Icons
import { ContactSupport, FormatListBulletedOutlined } from "@mui/icons-material";
import HistoryIcon from '@mui/icons-material/History';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import DashboardSharpIcon from '@mui/icons-material/DashboardSharp';
import LogoutIcon from '@mui/icons-material/Logout';
import VerifiedIcon from '@mui/icons-material/Verified';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import DescriptionIcon from '@mui/icons-material/Description';

// Lazy Loaded
import DashboardPage from "../pages/DashboardPage/DashboardPage.lazy";
import OrdersPage from "../pages/OrdersPage/OrdersPage.lazy";
import OrderHistoryXSeriesCarts from "../components/OrdersPage/OrderHistoryCarts/OrderHistoryCarts.lazy";
import PreviousOrderXSeriesCart from "../components/OrdersPage/PreviousOrderCart/PreviousOrderCart.lazy";
import OrderHistoryDisplays from "../components/OrdersPage/OrderHistoryDisplays/OrderHistoryDisplays.lazy";
import DealerDisplayOrderForm from "../components/DealerDisplayOrderForm/DealerDisplayOrderForm.lazy";
import PreviousOrderDealerDisplays from "../components/OrdersPage/PreviousOrderDealerDisplays/PreviousOrderDealerDisplays.lazy";
import OrderHistoryPrintedMaterials from "../components/OrdersPage/OrderHistoryPrintedMaterials/OrderHistoryPrintedMaterials.lazy";
import PrintMaterialOrderForm from "../components/OrdersPage/PrintMaterialOrderForm/PrintMaterialOrderForm.lazy";
import PreviousOrderPrintedMaterials from "../components/OrdersPage/PreviousOrderPrintedMaterials/PreviousOrderPrintedMaterials.lazy";
import WarrantyRegistrationPage from "../pages/WarrantyRegistrationPage/WarrantyRegistrationPage.lazy";
import WarrantyClaimsPage from "../pages/WarrantyClaimsPage/WarrantyClaimsPage.lazy";
import WarrantyClaimDetail from "../components/WarrantyClaimsPage/WarrantyClaimDetail/WarrantyClaimDetail.lazy";
import WarrantyClaimForm from "../components/WarrantyClaimsPage/WarrantyClaimForm/WarrantyClaimForm.lazy";
import TechSupportPage from "../pages/TechSupportPage/TechSupportPage.lazy";
import TechSupportDetail from "../components/TechSupportPage/TechSupportDetail/TechSupportDetail.lazy";
import TechSupportForm from "../components/TechSupportPage/TechSupportForm/TechSupportForm.lazy";
import DealerResourcesPage from "../pages/DealerResourcesPage/DealerResourcesPage.lazy";
import DealerProfilePage from "../pages/DealerProfilePage/DealerProfilePage.lazy";
import InvoicesPage from "../pages/InvoicesPage/InvoicesPage.lazy";
import InvoiceDetail from "../components/InvoicePage/InvoiceDetail/InvoiceDetail.lazy";
import McoLsvOrderForm from "../components/OrdersPage/McoLsvOrderForm/McoLsvOrderForm.lazy";
import OrderHistoryMco from "../components/OrdersPage/OrderHistoryMco/OrderHistoryMco.lazy";
import PreviousOrderMco from "../components/OrdersPage/PreviousOrderMco/PreviousOrderMco.lazy";
import CartOrderPage from "../pages/CartOrderPage/CartOrderPage.lazy";
import LeadsPage from "../pages/LeadsPage/LeadsPage.lazy";
import LeadDetail from "../components/LeadDetail/LeadDetail.lazy";

// Tour Steps
import {
    DealerProfile,
    TechSupportHistory,
    TourStepsDashboard,
    TourStepsOrdersPrintMedia,
    TourStepsOrderHistory,
    TourStepsCartOrderPage,
    TourStepsCartOrderPageWInventory,
    WarrantyClaimHistory,
    TourStepsLeads,
    TourStepsLeadsDetail
} from "../components/TourSteps";
import PreviousOrderMerchandise from "../components/OrdersPage/PreviousOrderMerchandise/PreviousOrderMerchandise";
import OrderHistoryMerchandise from "../components/OrdersPage/OrderHistoryPop/OrderHistoryPop";
import MerchandiseOrderForm from "../components/OrdersPage/PopOrderForm/PopOrderForm.lazy";



export const DashboardRoutes: RouteType[] = [
    {
        index: true,
        element: <Navigate to="/dashboard" />,
        state: "dashboard",
        displayText: 'Dashboard'
    },
    {
        path: "/dashboard",
        element: <DashboardPage />,
        state: "dashboard",
        displayText: 'Dashboard',
        tutorialType: 'Dashboard',
        hasTutorial: true,
        tutorialSteps: TourStepsDashboard,
        sidebarProps: {
            displayText: "Dashboard",
            icon: <DashboardSharpIcon />,
            id: "DashboardNavLink"
        }
    },
]

export const OrderRoutesSupport: RouteType[] = [
    {
        path: "/orders",
        element: <OrdersPage />,
        state: "orders",
        displayText: 'Orders',
        sidebarProps: {
            displayText: "Orders",
            icon: <HistoryIcon />,
            id: "OrdersNavLink"
        },
        child: [
            {
                index: true,
                element: <Navigate to="/print-media" />,
                state: "orders.print-media",
                displayText: 'Print Media',
            },
            {
                path: "/orders/print-media",
                element: <OrderHistoryPrintedMaterials />,
                state: "orders.print-media",
                displayText: 'Print Media',
                hasTutorial: true,
                tutorialType: 'Print Media',
                tutorialSteps: TourStepsOrdersPrintMedia,
            },
            {
                path: "/orders/print-media/new-order",
                element: <PrintMaterialOrderForm />,
                state: "orders.print-media.new-order",
                displayText: 'New Print Media Order',
                sidebarProps: {
                    displayText: "New Print Media Order",
                    id: "NewPrintedMaterialsOrder"
                }
            },
            {
                path: "/orders/print-media/:id",
                element: <PreviousOrderPrintedMaterials />,
                state: "orders.print-media.id",
                displayText: 'Order',
            },
            {
                path: "/orders/pop/:id",
                element: <PreviousOrderMerchandise />,
                state: "orders.pop.id",
                displayText: 'POP Order',
            },
            {
                path: "/orders/pop",
                element: <OrderHistoryMerchandise />,
                state: "orders.pop",
                displayText: 'POP',
            },
            {
                path: "/orders/pop/new-order",
                element: <MerchandiseOrderForm />,
                state: "orders.pop.new-order",
                displayText: 'New POP Order',
                sidebarProps: {
                    displayText: "New POP Order",
                    id: "NewPopOrder"
                }
            }
        ]
    }
]

export const OrderRoutesWInventory: RouteType[] = [
    {
        path: "/orders",
        element: <OrdersPage />,
        state: "orders",
        displayText: 'Orders',
        sidebarProps: {
            displayText: "Orders",
            icon: <HistoryIcon />,
            id: "OrdersNavLink"
        },
        child: [
            {
                index: true,
                element: <Navigate to="./carts" />,
                state: "orders.carts",
                displayText: 'Carts',
            },
            {
                path: "/orders/carts",
                element: <OrderHistoryXSeriesCarts />,
                state: "orders.carts",
                displayText: 'Carts',
                hasTutorial: true,
                tutorialType: 'Carts',
                tutorialSteps: TourStepsOrderHistory,
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                }
            },
            {
                path: "/orders/carts/new-order/view-inventory",
                element: <CartOrderPage />,
                state: "orders.carts.view-inventory",
                displayText: 'New Cart Order',
                hasTutorial: true,
                tutorialType: 'Cart Order',
                tutorialSteps: TourStepsCartOrderPageWInventory,
                sidebarProps: {
                    displayText: "View Inventory"
                }
            },
            {
                path: "/orders/carts/new-order",
                element: <CartOrderPage />,
                state: "orders.carts.new-order",
                displayText: 'New Cart Order',
                hasTutorial: true,
                tutorialType: 'Cart Order',
                tutorialSteps: TourStepsCartOrderPageWInventory,
                sidebarProps: {
                    displayText: "New Cart Order"
                }
            },
            {
                path: "/orders/carts/:id",
                element: <PreviousOrderXSeriesCart />,
                state: "orders.carts.id",
                displayText: 'Order',
            },
            {
                path: "/orders/displays",
                element: <OrderHistoryDisplays />,
                state: "orders.displays",
                displayText: 'Show Room Displays',
                hasTutorial: true,
                tutorialType: 'Displays',
                tutorialSteps: TourStepsOrderHistory,
            },
            {
                path: "/orders/displays/new-order",
                element: <DealerDisplayOrderForm />,
                state: "orders.displays.new-order",
                displayText: "New Showroom Display Order",
                sidebarProps: {
                    displayText: "New Display Order"
                }
            },
            {
                path: "/orders/displays/:id",
                element: <PreviousOrderDealerDisplays />,
                state: "orders.displays.id",
                displayText: 'Order',
            },
            {
                path: "/orders/print-media",
                element: <OrderHistoryPrintedMaterials />,
                state: "orders.print-media",
                displayText: 'Print Media',
                hasTutorial: true,
                tutorialType: 'Print Media',
                tutorialSteps: TourStepsOrdersPrintMedia,
            },
            {
                path: "/orders/print-media/new-order",
                element: <PrintMaterialOrderForm />,
                state: "orders.print-media.new-order",
                displayText: 'New Print Media Order',
                sidebarProps: {
                    displayText: "New Print Media Order",
                    id: "NewPrintedMaterialsOrder"
                }
            },
            {
                path: "/orders/print-media/:id",
                element: <PreviousOrderPrintedMaterials />,
                state: "orders.print-media.id",
                displayText: 'Order',
            },
            {
                path: "/orders/mco-lsv",
                element: <OrderHistoryMco />,
                state: "orders.mco-lsv",
                displayText: 'MCO | LSV',
            },
            {
                path: "/orders/mco-lsv/new-order",
                element: <McoLsvOrderForm />,
                state: "orders.mco-lsv.new-order",
                displayText: 'New MCO & LSV Kit Order',
                sidebarProps: {
                    displayText: "New MCO | LSV Order",
                    id: "NewMcoLSVOrder"
                }
            },
            {
                path: "/orders/mco-lsv/:id",
                element: <PreviousOrderMco />,
                state: "orders.mco-lsv.id",
                displayText: 'Order',
            },
            {
                path: "/orders/pop/:id",
                element: <PreviousOrderMerchandise />,
                state: "orders.pop.id",
                displayText: 'POP Order',
            },
            {
                path: "/orders/pop",
                element: <OrderHistoryMerchandise />,
                state: "orders.pop",
                displayText: 'POP',
            },
            {
                path: "/orders/pop/new-order",
                element: <MerchandiseOrderForm />,
                state: "orders.pop.new-order",
                displayText: 'New POP Order',
                sidebarProps: {
                    displayText: "New POP Order",
                    id: "NewPopOrder"
                }
            }
        ]
    }
]

export const OrderRoutes: RouteType[] = [
    {
        path: "/orders",
        element: <OrdersPage />,
        state: "orders",
        displayText: 'Orders',
        sidebarProps: {
            displayText: "Orders",
            icon: <HistoryIcon />,
            id: "OrdersNavLink"
        },
        child: [
            {
                index: true,
                element: <Navigate to="./carts" />,
                state: "orders.carts",
                displayText: 'Carts',
            },
            {
                path: "/orders/carts",
                element: <OrderHistoryXSeriesCarts />,
                state: "orders.carts",
                displayText: 'Carts',
                hasTutorial: true,
                tutorialType: 'Carts',
                tutorialSteps: TourStepsOrderHistory,
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                }
            },
            {
                path: "/orders/carts/new-order",
                element: <CartOrderPage />,
                state: "orders.carts.new-order",
                displayText: 'New Cart Order',
                hasTutorial: true,
                tutorialType: 'Cart Order',
                tutorialSteps: TourStepsCartOrderPage,
                sidebarProps: {
                    displayText: "New Cart Order"
                }
            },
            {
                path: "/orders/carts/:id",
                element: <PreviousOrderXSeriesCart />,
                state: "orders.carts.id",
                displayText: 'Order',
            },
            {
                path: "/orders/displays",
                element: <OrderHistoryDisplays />,
                state: "orders.displays",
                displayText: 'Show Room Displays',
                hasTutorial: true,
                tutorialType: 'Displays',
                tutorialSteps: TourStepsOrderHistory,
            },
            {
                path: "/orders/displays/new-order",
                element: <DealerDisplayOrderForm />,
                state: "orders.displays.new-order",
                displayText: "New Showroom Display Order",
                sidebarProps: {
                    displayText: "New Display Order"
                }
            },
            {
                path: "/orders/displays/:id",
                element: <PreviousOrderDealerDisplays />,
                state: "orders.displays.id",
                displayText: 'Order',
            },
            {
                path: "/orders/print-media",
                element: <OrderHistoryPrintedMaterials />,
                state: "orders.print-media",
                displayText: 'Print Media',
                hasTutorial: true,
                tutorialType: 'Print Media',
                tutorialSteps: TourStepsOrdersPrintMedia,
            },
            {
                path: "/orders/print-media/new-order",
                element: <PrintMaterialOrderForm />,
                state: "orders.print-media.new-order",
                displayText: 'New Print Media Order',
                sidebarProps: {
                    displayText: "New Print Media Order",
                    id: "NewPrintedMaterialsOrder"
                }
            },
            {
                path: "/orders/print-media/:id",
                element: <PreviousOrderPrintedMaterials />,
                state: "orders.print-media.id",
                displayText: 'Order',
            },
            {
                path: "/orders/mco-lsv",
                element: <OrderHistoryMco />,
                state: "orders.mco-lsv",
                displayText: 'MCO | LSV',
            },
            {
                path: "/orders/mco-lsv/new-order",
                element: <McoLsvOrderForm />,
                state: "orders.mco-lsv.new-order",
                displayText: 'New MCO & LSV Kit Order',
                sidebarProps: {
                    displayText: "New MCO | LSV Order",
                    id: "NewMcoLSVOrder"
                }
            },
            {
                path: "/orders/mco-lsv/:id",
                element: <PreviousOrderMco />,
                state: "orders.mco-lsv.id",
                displayText: 'Order',
            },
            {
                path: "/orders/pop/:id",
                element: <PreviousOrderMerchandise />,
                state: "orders.pop.id",
                displayText: 'POP Order',
            },
            {
                path: "/orders/pop",
                element: <OrderHistoryMerchandise />,
                state: "orders.pop",
                displayText: 'POP',
            },
            {
                path: "/orders/pop/new-order",
                element: <MerchandiseOrderForm />,
                state: "orders.pop.new-order",
                displayText: 'New POP Order',
                sidebarProps: {
                    displayText: "New POP Order",
                    id: "NewPopOrder"
                }
            }
        ]
    }
]

export const RegistrationRoutes: RouteType[] = [
    {
        path: "/registration",
        element: <WarrantyRegistrationPage />,
        state: "registration",
        displayText: 'Warranty Registration',
        sidebarProps: {
            displayText: "Warranty Registration",
            icon: <AppRegistrationIcon />,
            id: "WarrantyRegistrationNavLink"
        }
    }
]

export const WarrantyClaimRoutes: RouteType[] = [
    {
        path: "/warranty-claims",
        element: <WarrantyClaimsPage />,
        state: "warranty-claims",
        displayText: 'Warranty Claims',
        tutorialType: 'Warranty Claims',
        hasTutorial: true,
        tutorialSteps: WarrantyClaimHistory,
        sidebarProps: {
            displayText: "Warranty Claims",
            icon: <VerifiedIcon />,
            id: "WarrantyClaimsNavLink"
        },
        child: [
            {
                path: "warranty-claims",
                element: <Navigate to=".." />,
                state: "warranty-claims.view-all",
                displayText: 'Warranty Claims',
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                }
            },
            {
                path: "/warranty-claims/:id",
                element: <WarrantyClaimDetail />,
                state: "warranty-claims.id",
                displayText: 'Warranty Claim',
            },
            {
                path: "/warranty-claims/new-ticket",
                element: <WarrantyClaimForm />,
                state: "warranty-claims.new-ticket",
                displayText: "New Warranty Claim",
                sidebarProps: {
                    displayText: "New Claim",
                    id: "NewWarrantyClaim"
                }
            }
        ]
    }
]

export const TechSupportRoutes: RouteType[] = [
    {
        path: "/technical-support",
        element: <TechSupportPage />,
        state: "technical-support",
        displayText: 'Technical Support',
        tutorialType: 'Tech Support',
        hasTutorial: true,
        tutorialSteps: TechSupportHistory,
        sidebarProps: {
            displayText: "Technical Support",
            icon: <ContactSupport />,
            id: "TechSupportNavLink"
        },
        child: [
            {
                path: "technical-support",
                element: <Navigate to=".." />,
                state: "technical-support.view-all",
                displayText: 'Technical Support',
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                }
            },
            {
                path: "/technical-support/:id",
                element: <TechSupportDetail />,
                state: "technical-support.id",
                displayText: 'Technical Support',
            },
            {
                path: "/technical-support/new-ticket",
                element: <TechSupportForm />,
                state: "technical-support.new-ticket",
                displayText: "New Technical Support Ticket",
                sidebarProps: {
                    displayText: "New Support Ticket"
                }
            }
        ]
    },
]

export const FinancialRoutes: RouteType[] = [
    {
        path: '/invoices',
        element: <InvoicesPage />,
        state: 'invoices',
        displayText: 'Invoices',
        sidebarProps: {
            displayText: 'Invoices',
            icon: <DescriptionIcon />,
            id: 'InvoicesNavLink'
        },
        child: [
            {
                index: true,
                path: "invoices",
                element: <Navigate to=".." />,
                state: "invoices",
                displayText: 'Invoices',
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                }
            },
            {
                path: "/invoices/:id",
                element: <InvoiceDetail />,
                state: "invoices.id",
                displayText: 'Invoice',
            },
        ]
    },
    {
        path: "https://www.myaccountdata.com/en/dllgroup/Home",
        externalPath: true,
        element: <></>,
        state: "dll",
        displayText: 'DLL Account',
        sidebarProps: {
            displayText: "DLL Account",
            icon: <AccountBalanceIcon />,
            id: "DDLNavLink"
        },
    }
]

export const LeadsRoutes: RouteType[] = [
    {
        path: "/leads",
        element: <LeadsPage />,
        state: "leads",
        displayText: 'Leads',
        sidebarProps: {
            displayText: "Leads",
            icon: <ConnectWithoutContactIcon />,
            id: "LeadsNavLink"
        },
        hasTutorial: true,
        tutorialSteps: TourStepsLeads,
        child: [
            {
                path: "/leads",
                element: <Navigate to=".." />,
                state: "leads.view-all",
                displayText: "Leads",
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                },
                hasTutorial: true,
                tutorialSteps: TourStepsLeads
            },
            {
                path: "/leads/:id",
                element: <LeadDetail />,
                state: "leads.id",
                displayText: "Lead",
                hasTutorial: true,
                tutorialSteps: TourStepsLeadsDetail
            }
        ]
    }
]

export const ResourcesRoutes: RouteType[] = [
    {
        path: "/resources",
        element: <DealerResourcesPage />,
        state: "resources",
        displayText: 'Resources',
        sidebarProps: {
            displayText: "Resources",
            icon: <FormatListBulletedOutlined />,
            id: "ResourcesNavLink"
        }
    }
]

export const GeneralRoutes: RouteType[] = [
    {
        path: "/login",
        element: <></>,
        state: "logout",
        displayText: 'Logout',
        sidebarProps: {
            displayText: "Logout",
            icon: <LogoutIcon />,
            id: "LogoutNavLink"
        },
    },
    {
        path: "/dealer-profile",
        element: <DealerProfilePage />,
        state: "dealer-profile",
        displayText: 'Dealer Profile',
        tutorialType: 'Dealer Profile',
        hasTutorial: true,
        tutorialSteps: DealerProfile,
    }
]